import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Premium wizard autosave hook.
 *  - Stores wizard payload in localStorage instantly on every change.
 *  - When a profileId exists, debounces a POST /api/profiles/{id}/autosave
 *    to persist the draft server-side for cross-device resume.
 *  - For NEW profiles (no profileId yet) it only uses localStorage.
 *
 * @returns {{ status: 'idle'|'saving'|'saved'|'error', savedAt: string|null,
 *            restoreDraft: () => any, clearLocalDraft: () => void,
 *            clearServerDraft: () => Promise<void> }}
 */
export function useAutosave({ profileId, ownerId, payload, step, enabled = true }) {
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);
  const lastSerializedRef = useRef("");

  const lsKey = `rx_wizard_draft:${ownerId || "anon"}:${profileId || "new"}`;

  // Local persistence (instant)
  useEffect(() => {
    if (!enabled) return;
    try {
      const ser = JSON.stringify({ payload, step, ts: Date.now() });
      if (ser !== lastSerializedRef.current) {
        lastSerializedRef.current = ser;
        localStorage.setItem(lsKey, ser);
      }
    } catch (e) {
      console.warn("[useAutosave] localStorage write failed:", e);
    }
  }, [payload, step, enabled, lsKey]);

  // Debounced server autosave (only for existing profiles)
  useEffect(() => {
    if (!enabled || !profileId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setStatus("saving");
      try {
        const { data } = await api.post(`/profiles/${profileId}/autosave`, { payload, step });
        setStatus("saved");
        setSavedAt(data.saved_at || new Date().toISOString());
      } catch (e) {
        console.warn("[useAutosave] server save failed:", e);
        setStatus("error");
      }
    }, 8000);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [payload, step, profileId, enabled]);

  const restoreDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  }, [lsKey]);

  const clearLocalDraft = useCallback(() => {
    try { localStorage.removeItem(lsKey); } catch (e) { console.warn(e); }
  }, [lsKey]);

  const clearServerDraft = useCallback(async () => {
    if (!profileId) return;
    try { await api.delete(`/profiles/${profileId}/autosave`); } catch (e) { console.warn(e); }
  }, [profileId]);

  return { status, savedAt, restoreDraft, clearLocalDraft, clearServerDraft };
}
